import SvgClapperboard from "@uui/Clapperboard";
import SvgMail05 from "@uui/Mail05";
import SvgMessageTextSquare02 from "@uui/MessageTextSquare02";
import SvgRecording03 from "@uui/Recording03";
import SvgSettings01 from "@uui/Settings01";
import SvgShare07 from "@uui/Share07";
import { stubTrue } from "lodash/fp";

import {
  AssistantDisplayOption,
  AssistantNameOption,
  AssistantLogoOption,
} from "@app/analytics/account/types";

import JoinPreferences from "./WorkspaceWizard/settingsModals/JoinPreferences";
import MemberSettings from "./WorkspaceWizard/settingsModals/MemberSettings";
import ShareTeamData from "./WorkspaceWizard/settingsModals/ShareTeamData";
import type {
  GetLabelFn,
  WorkspaceSettingConfig,
} from "./WorkspaceWizard/types";

export type WorkspaceSettingCategory = "report" | "sharing" | "access";

export const ASSISTANT_DISPLAY_OPTIONS: AssistantDisplayOption[] = [
  {
    value: "display_off",
    label: "Assistant name only",
    default: true,
  },
  {
    value: "display_on_metrics_off",
    label: "Logo (Read or Custom)",
    default: false,
  },
  {
    value: "display_on_metrics_on",
    label: "Meeting Metrics",
    default: false,
  },
];

export const ASSISTANT_LOGO_OPTIONS: AssistantLogoOption[] = [
  {
    value: "false",
    label: "Read Logo",
    default: true,
  },
  {
    value: "true",
    label: "Custom Logo",
    default: false,
  },
];

export const ASSISTANT_NAME_CONFIG: {
  title: string;
  subtitle: string;
  name: string;
  options: AssistantNameOption[];
} = {
  title: "Assistant Name",
  subtitle:
    "This will appear in the Assistant's participant window during a meeting.",
  name: "botNameMode",
  options: [
    {
      id: "read_ai_meeting_notes",
      displayName: () => "read.ai meeting notes",
      default: true,
      isEnabled: stubTrue,
    },
    {
      id: "meeting_analytics_from_read",
      displayName: () => "Meeting Analytics from Read",
      default: false,
      isEnabled: stubTrue,
    },
    {
      id: "assistant",
      displayName: (user) =>
        user ? `${user.firstName}'s Assistant` : "[Host Name]'s Assistant",
      default: false,
      isEnabled: stubTrue,
    },
    {
      id: "notetaker",
      displayName: (user) =>
        user ? `${user.firstName}'s Notetaker` : "[Host Name]'s Notetaker",
      default: false,
      isEnabled: stubTrue,
    },
    {
      id: "custom",
      displayName: () => "Custom",
      default: false,
      isEnabled: (inWorkspace) => inWorkspace,
    },
  ],
};

export const ASSISTANT_DISPLAY_CONFIG = {
  title: "Assistant Display",
  name: "botConfigIsUserEditable" as const,
};

export const MEETING_ROLE = {
  title: "Meeting Role",
  name: "calendarPermissionLevel",
  options: [
    {
      label: "Allow Read to join all calendar invites",
      value: "all",
    },
    {
      label: "Allow Read to join calendar invites when user is the host",
      value: "hosting_only",
    },
    {
      label: "Users manually select the meetings Read joins",
      value: "invite_only",
    },
  ],
};

export const MEETING_TYPES = {
  title: "Meeting Types",
  name: "calendarJoinInternalOnly",
  options: [
    {
      label: "Allow Read to join all meeting types",
      value: "false",
    },
    {
      label: "Allow Read to join internal meetings only",
      value: "true",
    },
  ],
};

export const MEMBERSHIP_FIELDS = {
  INVITATIONS: {
    title: "Manage Members",
    subtitle:
      "Who can invite members to the workspace? Only admins can remove members.",
    name: "workspaceInvitations",
    options: [
      {
        label: "Admin & Manager can invite users",
        value: "manager",
      },
      {
        label: "Only Admins can invite users",
        value: "admin",
      },
    ],
  },
  TEAM_PERMISSION_RESOLUTION: {
    title: "Multi-Team Permissions",
    subtitle:
      "For users on multiple teams with conflicting permissions, which are honored?",
    name: "useLeastPermissivePermissionResolution",
    options: [
      {
        label: "Honor least permissive (most restrictive)",
        value: "true",
      },
      {
        label: "Honor most permissive (least restrictive)",
        value: "false",
      },
    ],
  },
};

export const MEETING_RECAP_VALUE = "Meeting Recaps";
export const EMAIL_DIGEST_FREQUENCY = {
  title: "Email Digest Frequency",
  multiSelect: {
    selectAll: { label: "All digests", isDefault: true },
    items: [
      { label: "After Every Meeting", value: "Meeting Recaps" },
      { label: "Weekly Digest", value: "Weekly Recaps" },
    ],
  },
};

const getJoinPreferencesLabel: GetLabelFn = (s) => {
  const joinText = s.calendarJoinInternalOnly ? "internal " : "";
  switch (s.calendarPermissionLevel) {
    case "hosting_only":
      return `Join ${joinText}meetings we host`;
    case "invite_only":
      return `Join ${joinText}meetings on invite`;
    case "all":
    default:
      return `Join all ${joinText}meetings`;
  }
};

const getShareTeamDataLabel: GetLabelFn = (s) => {
  return s.shareTeamDataView === "member"
    ? "Yes, automatically share"
    : s.shareTeamDataView === "manager"
      ? "Share only with Team Managers"
      : "No, don't automatically share";
};

const joinPreferences: WorkspaceSettingConfig = {
  id: "joinPreferences",
  title: "Read join preferences",
  description:
    "Which meetings should Read Assistant automatically join to generate meeting reports?",
  Icon: SvgRecording03,
  Modal: JoinPreferences,
  getLabel: getJoinPreferencesLabel,
};

const transcriptionOptIn: WorkspaceSettingConfig = {
  id: "transcriptionOptIn",
  title: "Meeting transcription and notes",
  description:
    "Enable transcripts, meeting summary, topics, action items, and key questions in reports.",
  Icon: SvgMessageTextSquare02,
  getLabel: (s) =>
    s.transcriptionOptIn ? "Enabled (recommended)" : "Disabled",
};

const storeRecordingOptIn: WorkspaceSettingConfig = {
  id: "storeRecordingOptIn",
  title: "Audio and video playback",
  description:
    "Enable audio and video playback with highlights and reactions in reports.",
  Icon: SvgClapperboard,
  getLabel: (s) =>
    s.storeRecordingOptIn ? "Enabled (recommended)" : "Disabled",
};

const sendMeetingRecap: WorkspaceSettingConfig = {
  id: "sendMeetingRecap",
  title: "Send meeting recaps",
  description:
    "Who should be sent recaps after a meeting via distribution channels like email, Slack, or Teams?",
  Icon: SvgMail05,
  getLabel: (s) => (s.sendMeetingRecap ? "Enabled (recommended)" : "Disabled"),
};

const shareTeamDataView: WorkspaceSettingConfig = {
  id: "shareTeamDataView",
  title: "Team report access",
  description:
    "Should people in a team automatically have access to everyone else's reports?",
  Icon: SvgShare07,
  Modal: ShareTeamData,
  getLabel: getShareTeamDataLabel,
};

const memberSettings: WorkspaceSettingConfig = {
  id: "memberSettings",
  title: "Member settings",
  description: "Allow members to update their settings?",
  Icon: SvgSettings01,
  Modal: MemberSettings,
  getLabel: (s) =>
    s.storeRecordingOptInIsUserEditable
      ? "Yes, only to more restrictive options"
      : "No, don't allow changes",
};

export const WORKSPACE_SETTINGS: {
  [key in WorkspaceSettingCategory]: WorkspaceSettingConfig[];
} = {
  report: [joinPreferences, transcriptionOptIn, storeRecordingOptIn],
  sharing: [sendMeetingRecap],
  access: [shareTeamDataView, memberSettings],
};
